import { Component } from "react";
import WidgetPanel from "components/common/WidgetPanel";
import "components/common/charts/styles/Charts.css";
import PromineoChart from "components/common/charts/PromineoChart";
import {
  Size,
  Series,
  ArgumentAxis,
  CommonSeriesSettings,
  Tooltip,
  ValueAxis,
  Label,
  Point,
  Crosshair,
  TickInterval,
} from "devextreme-react/chart";
import { AppDispatch } from "store/store";
import { connect } from "react-redux";
import DailyConsumptionSummary from "interfaces/response/DailyConsumptionSummary";
import moment, { Moment } from "moment";
import ActionSummaryFilter from "./ActionSummaryFilter";
import { ActionSummaryChartFilterConfig } from "interfaces/ActionSummaryChartFilterConfig";
import { getDateTime } from "shared/utility/DateTimeUtility";
import { ChartGranularity } from "shared/enums/ChartGranularityEnum";
import customColors from "Colors";
import { getFormattedNumber } from "shared/utility/NumberUtility";

interface Props {
  consumptionSummaryData?: DailyConsumptionSummary[];
  actionSummaryChartFilterConfig: ActionSummaryChartFilterConfig;
  licenseKeyTags: string[];
  dispatch: AppDispatch;
  onTagSelectionChange: (tags: string[]) => void;
  onChartViewModeChange: (viewMode: string) => void;
  onDateRangeChange: (fromDate: Moment, toDate: Moment) => void;
}

export class ActionSummaryChartWidget extends Component<Props, any> {
  isYearlyView = () => {
    const viewMode = this.props.actionSummaryChartFilterConfig.chartViewMode;
    return viewMode === ChartGranularity.Yearly;
  };

  ChartTooltip = ({ data }: any) => {
    const date = moment(data.argument);
    const dateText = this.isYearlyView()
      ? date.format("MMMM, YYYY")
      : date.format("MMMM DD");
    const actions: number = data.value;
    return (
      <div className="font-inter">
        <div className="font-semibold">{dateText}</div>
        <span className="text-promGray2 mr-6">actions: </span>
        {getFormattedNumber(actions)}
      </div>
    );
  };

  getSummaryFilter = () => {
    const handleTagSelectionChange = (selectedTags: string[]) => {
      this.props.onTagSelectionChange(selectedTags);
    };

    const handleChartViewModeChange = (viewMode: string) => {
      this.props.onChartViewModeChange(viewMode);
    };

    const handleFilterDateChange = (fromDate: Moment, toDate: Moment) => {
      this.props.onDateRangeChange(fromDate, toDate);
    };

    return (
      <ActionSummaryFilter
        onTagSelectionChange={handleTagSelectionChange}
        onViewModeChange={handleChartViewModeChange}
        onFilterDateChange={handleFilterDateChange}
        tagItems={this.props.licenseKeyTags}
        selectedTagItems={
          this.props.actionSummaryChartFilterConfig.selectedTags
        }
        chartViewMode={this.props.actionSummaryChartFilterConfig.chartViewMode}
        fromDate={getDateTime(
          this.props.actionSummaryChartFilterConfig.fromDate
        )}
        toDate={getDateTime(this.props.actionSummaryChartFilterConfig.toDate)}
      />
    );
  };

  getFormattedValueAxisValue = (value: number) => {
    return getFormattedNumber(value);
  }

  render() {
    return (
      <WidgetPanel
        title="Actions over time"
        titleForSmallScreen="Last 30 days"
        headerWidget={this.getSummaryFilter()}
        displaySecondaryHeaderOnSmallScreen
      >
        <div className="h-72">
          <PromineoChart
            chartOptions={{
              palette: "Material",
              dataSource: this.props.consumptionSummaryData,
              legend: { visible: false },
            }}
          >
            <Size height={288} />

            <ArgumentAxis
              grid={{ visible: false }}
              argumentType="datetime"
              tick={{ visible: false }}
              color={customColors.silver}
              aggregationInterval={{
                months: 1,
              }}
            >
              {this.isYearlyView() ? (
                <TickInterval months={1} />
              ) : (
                <TickInterval days={1} />
              )}
            </ArgumentAxis>

            <ValueAxis type="number" visible={false} tick={{ visible: false }}>
              <Label format={this.getFormattedValueAxisValue} />
            </ValueAxis>

            <CommonSeriesSettings>
              <Point size="6" hoverMode="onlyPoint" />
            </CommonSeriesSettings>

            <Crosshair
              enabled={true}
              dashStyle="5,2"
              color={customColors.promBlue1}
              horizontalLine={{ visible: false }}
            />

            <Series
              type="line"
              argumentField="date"
              valueField="actions"
              color={customColors.primaryRed}
              aggregation={{
                enabled: this.isYearlyView(),
                method: "sum",
              }}
            />

            <Tooltip
              enabled={true}
              contentComponent={this.ChartTooltip}
              paddingTopBottom="8"
              paddingLeftRight="12"
              border={{ color: customColors.white }}
              cornerRadius="4"
              arrowLength="0"
              shadow={{
                offsetX: 0,
                offsetY: 2,
                blur: 9,
                color: "rgba(0, 0, 0, 0.15)",
              }}
            />
          </PromineoChart>
        </div>
      </WidgetPanel>
    );
  }
}

export default connect()(ActionSummaryChartWidget);
